import { ColumnDef } from '@tanstack/react-table';
import { formatDistanceToNowStrict } from 'date-fns';
import { t } from 'i18next';
import { Loader, RefreshCw } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import i18n from '@/core/i18n';
import {
  getIntegrationStatusBadgeVariant,
  getIntegrationStatusText,
} from '@/modules/common/components/molecules/badge/status-configs/integration-status-config';
import {
  Integration,
  IntegrationServices,
  IntegrationState,
} from '@/modules/common/types/integrations';
import { getServiceIcon } from '@/modules/onboarding/integrations-setup/helpers/data';
import { formatIntegrationDataTypes } from '@/modules/settings/utils/integration-formatters';

interface GetColumnsParams {
  onRefresh: (service: IntegrationServices) => void;
  onConnect: (service: IntegrationServices) => void;
  refreshingService: IntegrationServices | null;
}

export const getIntegrationsTableColumns = ({
  onRefresh,
  onConnect,
  refreshingService,
}: GetColumnsParams): ColumnDef<Integration>[] => [
  {
    id: 'integration',
    header: () => (
      <div className="min-w-[170px]">
        {i18n.t('authenticated.settings:pages.integrations.table.columns.integration')}
      </div>
    ),
    cell: ({ row }) => (
      <div className="flex min-w-[170px] items-center gap-2">
        <img
          alt={row.original.service}
          src={getServiceIcon(row.original.service)}
          className="size-6"
        />
        <span className="font-medium">{row.original.service}</span>
      </div>
    ),
  },
  {
    id: 'status',
    header: () => (
      <div className="min-w-[100px]">
        {i18n.t('authenticated.settings:pages.integrations.table.columns.status')}
      </div>
    ),
    cell: ({ row }) => {
      const state = row.original.state;
      const badgeVariant = getIntegrationStatusBadgeVariant(state);
      const badgeText = getIntegrationStatusText(state);

      return (
        <div className="min-w-[120px]">
          <Badge variant={badgeVariant} className={'text-xs font-medium'}>
            {badgeText}
          </Badge>
        </div>
      );
    },
  },
  {
    id: 'type',
    header: () => (
      <div className="min-w-[200px]">
        {i18n.t('authenticated.settings:pages.integrations.table.columns.type')}
      </div>
    ),
    cell: ({ row }) => (
      <div className="min-w-[200px]">
        {formatIntegrationDataTypes(row.original.dataTypes) ?? t('common:table.noValue')}
      </div>
    ),
  },
  {
    id: 'action',
    header: () => (
      <div className="min-w-[250px] pr-4 text-right">
        {i18n.t('authenticated.settings:pages.integrations.table.columns.action')}
      </div>
    ),
    cell: ({ row }) => {
      const service = row.original.service;
      const state = row.original.state;
      const lastSyncedAt = row.original.lastSyncedAt;
      const isRefreshing = refreshingService === service || state === IntegrationState.SYNCING;

      const renderActionButton = () => {
        if (
          state === IntegrationState.CONNECTED ||
          state === IntegrationState.SYNCING ||
          state === IntegrationState.ERROR_CONNECTION
        ) {
          return (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRefresh(service)}
              disabled={isRefreshing}
            >
              {isRefreshing ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  {i18n.t('authenticated.settings:pages.integrations.table.actions.refreshing')}
                </>
              ) : (
                <>
                  <RefreshCw />{' '}
                  {i18n.t('authenticated.settings:pages.integrations.table.actions.refresh')}
                </>
              )}
            </Button>
          );
        }

        if (state === IntegrationState.NOT_CONNECTED) {
          return (
            <Button type="button" size="sm" variant="outline" onClick={() => onConnect(service)}>
              {i18n.t('authenticated.settings:pages.integrations.table.actions.connect')}
            </Button>
          );
        }

        return null;
      };

      const renderLastSync = () => {
        if (state === IntegrationState.CONNECTED && lastSyncedAt) {
          const timeAgo = formatDistanceToNowStrict(lastSyncedAt, { addSuffix: true });
          return (
            <span className="mr-2 text-xs text-muted-foreground">
              {i18n.t('authenticated.settings:pages.integrations.table.refreshedAgo', {
                time: timeAgo,
              })}
            </span>
          );
        }
        return null;
      };

      return (
        <div className="flex min-w-[250px] items-center justify-end gap-2 px-4 py-2">
          {renderLastSync()}
          {renderActionButton()}
        </div>
      );
    },
  },
];
